import { motion } from "framer-motion";
import { ArrowRight, Download, BookOpen, MessageCircle, Globe } from "lucide-react";

const PREVIEW = "/flyers/esl_preview.jpg";

const FLYERS = [
  { lang: "English · French", file: "/flyers/RBICA_ESL_Flyer_English-French.pdf", short: "FR" },
  { lang: "English · Spanish", file: "/flyers/RBICA_ESL_Flyer_English-Spanish.pdf", short: "ES" },
  { lang: "English · Farsi", file: "/flyers/RBICA_ESL_Flyer_English-Farsi.pdf", short: "FA" },
  { lang: "English · Chinese", file: "/flyers/RBICA_ESL_Flyer_English-Chinese.pdf", short: "ZH" },
];

export const EslBanner = () => {
  return (
    <section
      id="esl-program"
      data-testid="esl-banner-section"
      className="relative py-20 md:py-28 bg-[#E8F0F8] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#0B2B40] text-white px-4 py-2 text-[10px] tracking-[0.3em] uppercase font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E8F0F8] animate-pulse" />
            Now Enrolling · ESL Program
          </div>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl text-[#0B2B40] tracking-tight leading-[1.05] font-light">
            Learn English.{" "}
            <span className="italic">Build your new life.</span>
          </h2>
          <p className="mt-5 text-lg md:text-xl text-[#4A5568] font-light max-w-2xl mx-auto leading-relaxed">
            Real-world English coaching for adults new to the U.S. — flexible
            schedules, real-life practice, and the confidence to be heard.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Flyer preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-6"
          >
            <a
              href="/flyers/RBICA_ESL_Flyer_English-French.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="esl-flyer-preview-link"
              className="block relative group overflow-hidden shadow-[0_30px_80px_rgba(11,43,64,0.18)]"
            >
              <img
                src={PREVIEW}
                alt="RBICA ESL Program Flyer"
                className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 flex items-end justify-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-black/40 to-transparent">
                <div className="bg-white text-[#0B2B40] px-4 py-2 text-xs tracking-wider uppercase font-medium inline-flex items-center gap-2">
                  <Download size={13} /> View PDF
                </div>
              </div>
            </a>
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-6"
          >
            <div className="bg-white p-8 md:p-10 border border-white shadow-[0_18px_50px_rgba(11,43,64,0.06)]">
              {/* Two tracks */}
              <div className="text-xs tracking-[0.3em] uppercase text-[#1D4E89] font-medium mb-5">
                Two Program Tracks
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="border border-slate-100 p-5">
                  <div className="flex items-center gap-2 text-[#0B2B40]">
                    <BookOpen size={16} />
                    <h3 className="font-serif text-lg">Full Track</h3>
                  </div>
                  <p className="text-xs text-[#4A5568] mt-1">For beginners</p>
                  <ul className="mt-3 space-y-1.5 text-sm text-[#1A202C]">
                    <li>· Reading</li>
                    <li>· Writing</li>
                    <li>· Speaking</li>
                    <li>· Listening</li>
                  </ul>
                </div>
                <div className="border border-slate-100 p-5">
                  <div className="flex items-center gap-2 text-[#0B2B40]">
                    <MessageCircle size={16} />
                    <h3 className="font-serif text-lg">Advanced Track</h3>
                  </div>
                  <p className="text-xs text-[#4A5568] mt-1">Already conversational</p>
                  <ul className="mt-3 space-y-1.5 text-sm text-[#1A202C]">
                    <li>· Reading</li>
                    <li>· Writing</li>
                  </ul>
                </div>
              </div>

              {/* Pricing */}
              <div className="mt-8 pt-7 border-t border-slate-100">
                <div className="text-xs tracking-[0.3em] uppercase text-[#1D4E89] font-medium mb-4">
                  Schedule &amp; Pricing
                </div>
                <ul className="space-y-3">
                  <li className="flex items-baseline justify-between gap-4">
                    <div>
                      <div className="text-[#0B2B40] font-medium">Standard</div>
                      <div className="text-xs text-[#4A5568]">3 days/week · 1 hr/class</div>
                    </div>
                    <div className="font-serif text-2xl text-[#0B2B40]">$150<span className="text-sm text-[#4A5568]">/mo</span></div>
                  </li>
                  <li className="flex items-baseline justify-between gap-4">
                    <div>
                      <div className="text-[#0B2B40] font-medium">Intensive</div>
                      <div className="text-xs text-[#4A5568]">5 days/week · 1 hr/class</div>
                    </div>
                    <div className="font-serif text-2xl text-[#0B2B40]">$240<span className="text-sm text-[#4A5568]">/mo</span></div>
                  </li>
                </ul>

                <div className="mt-6 bg-[#F8F9FA] p-4 border-l-2 border-[#1D4E89]">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#4A5568] mb-2">
                    Prepay &amp; Save
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#1A202C]">
                    <span>3 mo · <b>5% off</b></span>
                    <span>· 6 mo · <b>10% off</b></span>
                    <span>· 9 mo · <b>15% off</b></span>
                    <span>· 12 mo · <b>20% off</b></span>
                  </div>
                </div>
              </div>

              {/* Formats */}
              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#4A5568]">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1D4E89]" />
                  Online via Zoom or Teams
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1D4E89]" />
                  In-Person · Seattle
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1D4E89]" />
                  Real-life practice outings
                </div>
              </div>

              <a
                href="/enroll/esl-coaching"
                data-testid="esl-enroll-button"
                className="mt-9 group inline-flex items-center justify-center gap-2 w-full bg-[#0B2B40] text-white hover:bg-[#1D4E89] px-9 py-4 text-sm font-medium tracking-wide transition-colors duration-300"
              >
                Enroll in the ESL Program
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
            </div>

            {/* Downloads */}
            <div className="mt-6 bg-[#0B2B40] text-white p-6 md:p-7">
              <div className="flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#E8F0F8] mb-4">
                <Globe size={14} /> Download the flyer
              </div>
              <div className="grid grid-cols-2 gap-2">
                {FLYERS.map((f) => (
                  <a
                    key={f.short}
                    href={f.file}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`esl-download-${f.short.toLowerCase()}`}
                    className="flex items-center justify-between gap-2 px-4 py-3 border border-white/20 hover:bg-white/10 transition-colors text-sm"
                  >
                    <span>{f.lang}</span>
                    <Download size={14} className="text-white/60" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default EslBanner;
